import React from 'react'
import { Modal, Form, Select, DatePicker, Input, Button, Steps, Row, Col, Typography, theme } from 'antd'
import { PlusOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { hexToRGBA } from '../../../../lib/utils'

const { Option } = Select
const { Title } = Typography
const { Step } = Steps

const DischargeModal = ({ isVisible, onCancel, onSubmit, form, filterValues }) => {
  const { token } = theme.useToken()
  const { departments } = filterValues || { departments: [] }

  return (
    <Modal
      title='Create Discharge Instructions'
      visible={isVisible}
      onCancel={onCancel}
      footer={[
        <Button
          key='cancel'
          onClick={onCancel}
          icon={<CloseOutlined style={{ color: token.colorError }} />}
          style={{
            background: hexToRGBA(token.colorError, 0.1),
            borderColor: token.colorError,
            color: token.colorError
          }}
        >
          Cancel
        </Button>,
        <Button
          key='submit'
          onClick={onSubmit}
          icon={<PlusOutlined style={{ color: token.colorSuccess }} />}
          style={{
            background: hexToRGBA(token.colorSuccess, 0.1),
            borderColor: token.colorSuccess,
            color: token.colorSuccess,
            fontWeight: 'bold'
          }}
        >
          Create Instructions
        </Button>
      ]}
      width={800}
    >
      <Form form={form} layout='vertical'>
        <Steps size='small' current={0} style={{ marginBottom: '24px' }}>
          <Step title='Patient Info' />
          <Step title='Diagnosis' />
          <Step title='Medications' />
          <Step title='Follow-up' />
          <Step title='Instructions' />
        </Steps>

        <Title level={5}>Patient Information</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='patientId'
              label='Patient'
              rules={[{ required: true, message: 'Please select a patient' }]}
            >
              <Select placeholder='Select patient' showSearch optionFilterProp='children'>
                <Option value='PT-1001'>John Smith (PT-1001)</Option>
                <Option value='PT-1023'>Emma Wilson (PT-1023)</Option>
                <Option value='PT-1045'>Michael Brown (PT-1045)</Option>
                {/* More options would be populated from patients database */}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name='dischargeDate'
              label='Discharge Date/Time'
              rules={[{ required: true, message: 'Please specify discharge date and time' }]}
            >
              <DatePicker showTime format='YYYY-MM-DD HH:mm' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='attendingPhysician'
              label='Attending Physician'
              rules={[{ required: true, message: 'Please select attending physician' }]}
            >
              <Select placeholder='Select physician'>
                <Option value='Dr. Sarah Johnson'>Dr. Sarah Johnson</Option>
                <Option value='Dr. Robert Chen'>Dr. Robert Chen</Option>
                <Option value='Dr. Emily Taylor'>Dr. Emily Taylor</Option>
                <Option value='Dr. James Wilson'>Dr. James Wilson</Option>
                {/* More options would be populated from staff database */}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name='department'
              label='Department'
              rules={[{ required: true, message: 'Please select department' }]}
            >
              <Select placeholder='Select department'>
                {departments.map((dept) => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name='diagnosisList'
          label='Diagnosis List'
          rules={[{ required: true, message: 'Please specify at least one diagnosis' }]}
        >
          <Select mode='tags' placeholder='Enter diagnoses'>
            <Option value='Pneumonia'>Pneumonia</Option>
            <Option value='Diabetes Mellitus'>Diabetes Mellitus</Option>
            <Option value='Hypertension'>Hypertension</Option>
            <Option value='Heart Failure'>Heart Failure</Option>
            <Option value='Urinary Tract Infection'>Urinary Tract Infection</Option>
            <Option value='Asthma'>Asthma</Option>
            {/* More options would be populated from diagnoses database */}
          </Select>
        </Form.Item>

        <Title level={5}>Medication Instructions</Title>
        <Form.List name='medications'>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Row gutter={16} key={field.key} style={{ marginBottom: '8px' }}>
                  <Col span={6}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'medication']}
                      label={field.key === 0 ? 'Medication' : ''}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder='Medication name' />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'dosage']}
                      label={field.key === 0 ? 'Dosage' : ''}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder='Dosage' />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'frequency']}
                      label={field.key === 0 ? 'Frequency' : ''}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder='Frequency' />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'duration']}
                      label={field.key === 0 ? 'Duration' : ''}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder='Duration' />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item
                      label={field.key === 0 ? 'Action' : ''}
                      style={{ marginTop: field.key === 0 ? '0' : '4px' }}
                    >
                      <Button
                        icon={<DeleteOutlined style={{ color: token.colorError }} />}
                        onClick={() => remove(field.name)}
                        style={{
                          background: hexToRGBA(token.colorError, 0.1),
                          borderColor: token.colorError
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button
                  icon={<PlusOutlined style={{ color: token.colorPrimary }} />}
                  onClick={() => add()}
                  block
                  style={{
                    borderStyle: 'dashed',
                    background: 'transparent',
                    color: token.colorPrimary
                  }}
                >
                  Add Medication
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='activityRestrictions'
              label='Activity Restrictions'
              rules={[{ required: true, message: 'Please specify activity restrictions' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name='dietaryRestrictions'
              label='Dietary Restrictions'
              rules={[{ required: true, message: 'Please specify dietary restrictions' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name='specialNotes'
          label='Special Instructions and Warning Signs'
          rules={[{ required: true, message: 'Please provide special instructions' }]}
        >
          <Input.TextArea rows={3} placeholder='Include warning signs and when to seek medical attention' />
        </Form.Item>

        <Form.Item name='woundCare' label='Wound Care Instructions (if applicable)'>
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default DischargeModal
